import {EVENT_ENDPOINTS, KNOWLEDGE_BASE_ENDPOINTS} from './endpoints';
import {fetchData} from "../../Utils/fetchData";
import authToken from "../../Utils/authToken";
import {ApiResponse} from "../../types/api.types";
import {IFile, IFileCreate, IFolder, IFolderCreate, IFolderUpdate} from "../../types/knowledgeBase.types";

export const KnowledgeBaseAPI = {
    fetchFolders: async (): Promise<ApiResponse<IFolder[]>> => {
        const response = await fetchData(KNOWLEDGE_BASE_ENDPOINTS.FETCH_FOLDERS, {
            method: "GET",
            headers: {
                Authorization: authToken(),
            },
        }, 2);

        return response.json();
    },

    createFolder: async (data: IFolderCreate): Promise<IFolder> => {
        // Всегда используем FormData для унификации формата запросов
        const formData = new FormData();
        formData.append('name', data.name);
        formData.append('accessType', data.accessType);

        // Добавляем файл обложки, если он есть
        if (data.coverFile) {
            formData.append('coverFile', data.coverFile);
        }

        // Добавляем роли или регионы в FormData по типу доступа
        if (data.accessType === 'role') {
            data.guestRoles.forEach((roleId, index) => {
                formData.append(`guestRoles[${index}]`, roleId);
            });
        } else if (data.accessType === 'region') {
            data.guestRegions.forEach((regionId, index) => {
                formData.append(`guestRegions[${index}]`, regionId);
            });
        }

        // Добавляем родительскую папку, если она есть
        if (data.parentFolder) {
            formData.append('parentFolder', data.parentFolder);
        }

        // Добавляем JSON-версию данных для совместимости с бэкендом
        const jsonData: any = {
            name: data.name,
            accessType: data.accessType,
            guestRoles: data.accessType === 'role' ? data.guestRoles : [],
            guestRegions: data.accessType === 'region' ? data.guestRegions : [],
            parentFolder: data.parentFolder
        };
        formData.append('jsonData', JSON.stringify(jsonData));

        const response = await fetchData(KNOWLEDGE_BASE_ENDPOINTS.CREATE_FOLDER, {
            method: 'POST',
            headers: {
                Authorization: authToken(),
            },
            body: formData
        }, 2);

        return response.json();
    },

    updateFolder: async (data: IFolderUpdate): Promise<ApiResponse<IFolder>> => {
        // Всегда используем FormData для унификации формата запросов
        const formData = new FormData();
        formData.append('_id', data._id);
        formData.append('name', data.name);
        formData.append('accessType', data.accessType);

        // Добавляем файл обложки, если он есть
        if (data.coverFile) {
            formData.append('coverFile', data.coverFile);
        }

        // Добавляем роли или регионы в FormData по типу доступа
        if (data.accessType === 'role') {
            data.guestRoles.forEach((roleId, index) => {
                formData.append(`guestRoles[${index}]`, roleId);
            });
        } else if (data.accessType === 'region') {
            data.guestRegions.forEach((regionId, index) => {
                formData.append(`guestRegions[${index}]`, regionId);
            });
        }

        // Добавляем JSON-версию данных для совместимости с бэкендом
        const jsonData: any = {
            _id: data._id,
            name: data.name,
            accessType: data.accessType,
            guestRoles: data.accessType === 'role' ? data.guestRoles : [],
            guestRegions: data.accessType === 'region' ? data.guestRegions : [],
        };
        formData.append('jsonData', JSON.stringify(jsonData));

        const response = await fetchData(KNOWLEDGE_BASE_ENDPOINTS.UPDATE_FOLDER, {
            method: 'PATCH',
            headers: {
                Authorization: authToken(),
            },
            body: formData
        }, 2);

        return response.json();
    },

    deleteFolder: async (folderId: string): Promise<ApiResponse> => {
        const response = await fetchData(`${KNOWLEDGE_BASE_ENDPOINTS.DELETE_FOLDER}/${folderId}`, {
            method: 'DELETE',
            headers: {
                "Content-type": "application/json",
                Authorization: authToken(),
            }
        }, 2);
        return response.json();
    },

    fetchFolderFiles: async (folderId: string): Promise<ApiResponse<Array<IFile | IFolder>>> => {
        const response = await fetchData(`${KNOWLEDGE_BASE_ENDPOINTS.FETCH_FOLDER_FILES}/${folderId}`, {
            method: "GET",
            headers: {
                Authorization: authToken(),
            },
        }, 2);

        return response.json();
    },

    uploadFile: async (folderId: string, data: IFileCreate): Promise<ApiResponse<IFile>> => {
        const formData = new FormData();
        if (data.file) {
            formData.append('file', data.file);
        }
        formData.append('name', data.name);

        const response = await fetchData(`${KNOWLEDGE_BASE_ENDPOINTS.UPLOAD_FILE}/${folderId}`, {
            method: 'POST',
            headers: {
                Authorization: authToken(),
            },
            body: formData
        }, 2);

        return response.json();
    },

    deleteFile: async (fileId: string): Promise<ApiResponse> => {

        const response = await fetchData(`${KNOWLEDGE_BASE_ENDPOINTS.DELETE_FILE}/${fileId}`, {
            method: 'DELETE',
            headers: {
                Authorization: authToken(),
            },
        }, 2);

        return response.json();
    },
    
    /**
     * Создает файл-ссылку в указанной папке
     * @param folderId ID папки, в которой создается ссылка
     * @param data Данные для создания ссылки (имя и URL)
     * @returns Информация о созданной ссылке
     */
    createLink: async (folderId: string, data: { name: string, link: string }): Promise<ApiResponse<IFile>> => {
        // Добавляем folderId в URL
        const url = `${KNOWLEDGE_BASE_ENDPOINTS.CREATE_LINK}/${folderId}`;
        const response = await fetchData(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: authToken(),
            },
            body: JSON.stringify(data)
        }, 2);

        return response.json();
    },
    
    /**
     * Получает информацию о папке по её ID
     * @param folderId ID папки
     * @returns Информация о папке
     */
    fetchFolderById: async (folderId: string): Promise<ApiResponse<IFolder>> => {
        const response = await fetchData(`${KNOWLEDGE_BASE_ENDPOINTS.FETCH_FOLDER_BY_ID}/${folderId}`, {
            method: 'GET',
            headers: {
                Authorization: authToken(),
            },
        }, 2);

        return response.json();
    },

    uploadVideo: async (folderId: string, data: IFileCreate): Promise<ApiResponse<IFile>> => {
        const formData = new FormData();
        if (data.file) {
            formData.append('file', data.file);
        }
        formData.append('name', data.name);
        formData.append('type', 'video');
        
        // Добавляем превью, если оно есть
        if (data.thumbnail) {
            formData.append('thumbnail', data.thumbnail);
        }

        const response = await fetchData(`${KNOWLEDGE_BASE_ENDPOINTS.UPLOAD_VIDEO}/${folderId}`, {
            method: 'POST',
            headers: {
                Authorization: authToken(),
            },
            body: formData
        }, 2);

        return response.json();
    },
};